import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { Archive, ArrowLeft, CalendarClock, CircleAlert, Send, Trash2 } from 'lucide-react'
import {
  getV1MeRetrieveQueryOptions,
  getV1PollsAdminPollsListQueryKey,
  getV1PollsAdminPollsPreviewRetrieveQueryKey,
  getV1PollsAdminPollsRetrieveQueryKey,
  useV1PollsAdminCapabilitiesRetrieve,
  useV1PollsAdminPollsArchiveCreate,
  useV1PollsAdminPollsCancelCreate,
  useV1PollsAdminPollsCancelScheduleCreate,
  useV1PollsAdminPollsDestroy,
  useV1PollsAdminPollsPreviewRetrieve,
  useV1PollsAdminPollsPublishCreate,
  useV1PollsAdminPollsResultsRetrieve,
  useV1PollsAdminPollsRetrieve,
  useV1PollsAdminPollsScheduleCreate,
  useV1PollsAdminPollsUpdate,
  type V1PollsAdminPollsArchiveCreateMutationResult,
  type V1PollsAdminPollsCancelCreateMutationResult,
  type V1PollsAdminPollsCancelScheduleCreateMutationResult,
  type V1PollsAdminPollsDestroyMutationResult,
  type V1PollsAdminPollsPublishCreateMutationResult,
  type V1PollsAdminPollsScheduleCreateMutationResult,
  type V1PollsAdminPollsUpdateMutationResult,
} from '@/api/client'
import type { AdminPoll } from '@/api/model'
import { failureBreakdownSummary } from '@/components/notifications/notification-labels'
import { PageHeader } from '@/components/page-header'
import {
  fromAdminPoll,
  pollCapabilitiesOrClosed,
  toAdminPollWriteRequest,
} from '@/components/push-poll/poll-api'
import { canAuthorPolls, canPublishPolls } from '@/components/push-poll/poll-capabilities'
import { PollCapabilityGate } from '@/components/push-poll/poll-capability-gate'
import { PollForm } from '@/components/push-poll/poll-form'
import { toPollWritePayload } from '@/components/push-poll/poll-form-schema'
import { PollPreviewDialog } from '@/components/push-poll/poll-preview-dialog'
import { PollResultsCard } from '@/components/push-poll/poll-results-card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

export const Route = createFileRoute('/_panel/zadania/ankiety/$uid')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: PollDetailPage,
})

function PollDetailPage() {
  const { uid } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [previewOpen, setPreviewOpen] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')
  const [cancelReason, setCancelReason] = useState('')
  const [draftDirty, setDraftDirty] = useState(false)
  const [actionError, setActionError] = useState<string>()
  const capabilitiesQuery = useV1PollsAdminCapabilitiesRetrieve()
  const capabilities = pollCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const pollQuery = useV1PollsAdminPollsRetrieve(uid)
  const poll = pollQuery.data?.status === 200 ? pollQuery.data.data : undefined
  const state = poll?.state
  const publishMutation = useV1PollsAdminPollsPublishCreate()
  const scheduleMutation = useV1PollsAdminPollsScheduleCreate()
  const cancelScheduleMutation = useV1PollsAdminPollsCancelScheduleCreate()
  const archiveMutation = useV1PollsAdminPollsArchiveCreate()
  const cancelMutation = useV1PollsAdminPollsCancelCreate()
  const destroyMutation = useV1PollsAdminPollsDestroy()
  const updateMutation = useV1PollsAdminPollsUpdate()
  const canPublish = canPublishPolls(capabilities)
  const previewQuery = useV1PollsAdminPollsPreviewRetrieve(uid, {
    query: { enabled: state === 'draft' && canPublish && !draftDirty },
  })
  const resultsQuery = useV1PollsAdminPollsResultsRetrieve(uid, {
    query: { enabled: state === 'published' || state === 'archived' || state === 'cancelled' },
  })
  const preview = previewQuery.data?.status === 200 ? previewQuery.data.data : undefined
  const results = resultsQuery.data?.status === 200 ? resultsQuery.data.data : undefined
  const pending = [
    publishMutation,
    scheduleMutation,
    cancelScheduleMutation,
    archiveMutation,
    cancelMutation,
    destroyMutation,
    updateMutation,
  ].some((mutation) => mutation.isPending)
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getV1PollsAdminPollsRetrieveQueryKey(uid) }),
      queryClient.invalidateQueries({ queryKey: getV1PollsAdminPollsListQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getV1PollsAdminPollsPreviewRetrieveQueryKey(uid) }),
    ])
  }
  const reportFailure = (label: string, status?: number, detail?: string) =>
    setActionError(
      status
        ? `${label}: ${detail ?? `serwer zwrócił HTTP ${status}`}.`
        : `${label}: sprawdź połączenie i spróbuj ponownie.`,
    )

  if (!poll) {
    return (
      <>
        <PageHeader title="Ankieta" />
        <div className="p-6">
          {pollQuery.isError ? (
            <p className="flex items-center gap-2 text-sm text-destructive">
              <CircleAlert /> Nie udało się pobrać ankiety.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Ładowanie…</p>
          )}
        </div>
      </>
    )
  }

  const previewPayload = toPollWritePayload(fromAdminPoll(poll))
  const validSchedule = Boolean(scheduledAt)
  const fail = (label: string, response: PollActionResponse) => {
    switch (response.status) {
      case 400:
      case 403:
      case 404:
      case 409:
      case 422:
        return reportFailure(label, response.status, response.data.message)
      default:
        return reportFailure(label, response.status)
    }
  }

  return (
    <>
      <PageHeader
        title={poll.name}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void navigate({ to: '/zadania/ankiety' })}
          >
            <ArrowLeft data-icon="inline-start" /> Lista ankiet
          </Button>
        }
      />
      <div className="flex max-w-6xl flex-col gap-6 p-6">
        {actionError && (
          <Alert variant="destructive">
            <AlertTitle>Akcja nie została wykonana</AlertTitle>
            <AlertDescription>{actionError}</AlertDescription>
          </Alert>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Stan: {state ?? 'draft'}</CardTitle>
            <CardDescription>
              Treść, zasięg i terminy zostają zamrożone po zaplanowaniu lub publikacji.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FieldGroup className="flex flex-wrap gap-2">
              {state === 'draft' && (
                <>
                  <Button
                    type="button"
                    disabled={!canPublish || !preview || draftDirty || pending}
                    onClick={() => setPreviewOpen(true)}
                  >
                    <Send data-icon="inline-start" /> Podgląd i publikacja
                  </Button>
                  <Field className="w-auto">
                    <FieldLabel htmlFor="poll-scheduled-at">Termin publikacji</FieldLabel>
                    <Input
                      id="poll-scheduled-at"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                    />
                    <FieldDescription>Wybierz przyszły termin publikacji.</FieldDescription>
                  </Field>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending || !canPublish || !validSchedule || draftDirty}
                    onClick={async () => {
                      if (new Date(scheduledAt).getTime() <= Date.now()) {
                        reportFailure('Termin planowania musi być w przyszłości')
                        return
                      }
                      setActionError(undefined)
                      try {
                        const response = await scheduleMutation.mutateAsync({
                          id: poll.id,
                          data: { scheduled_at: scheduledAt },
                        })
                        if (response.status !== 200)
                          return fail('Nie udało się zaplanować ankiety', response)
                        await refresh()
                      } catch {
                        reportFailure('Nie udało się zaplanować ankiety')
                      }
                    }}
                  >
                    <CalendarClock data-icon="inline-start" /> Zaplanuj
                  </Button>
                  {!canPublish && (
                    <FieldDescription>
                      Publikacja i planowanie wymagają capabilities publishing oraz runtime.
                    </FieldDescription>
                  )}
                  {draftDirty && (
                    <FieldDescription>
                      Zapisz szkic przed podglądem, publikacją lub planowaniem.
                    </FieldDescription>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending || !canAuthorPolls(capabilities)}
                    onClick={async () => {
                      setActionError(undefined)
                      try {
                        const response = await destroyMutation.mutateAsync({ id: poll.id })
                        if (response.status !== 204)
                          return fail('Nie udało się usunąć szkicu', response)
                        await navigate({ to: '/zadania/ankiety' })
                      } catch {
                        reportFailure('Nie udało się usunąć szkicu')
                      }
                    }}
                  >
                    <Trash2 data-icon="inline-start" /> Usuń szkic
                  </Button>
                </>
              )}
              {state === 'scheduled' && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={async () => {
                    setActionError(undefined)
                    try {
                      const response = await cancelScheduleMutation.mutateAsync({ id: poll.id })
                      if (response.status !== 200)
                        return fail('Nie udało się anulować planowania', response)
                      await refresh()
                    } catch {
                      reportFailure('Nie udało się anulować planowania')
                    }
                  }}
                >
                  Anuluj planowanie
                </Button>
              )}
              {(state === 'published' || state === 'cancelled') && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={async () => {
                    setActionError(undefined)
                    try {
                      const response = await archiveMutation.mutateAsync({ id: poll.id })
                      if (response.status !== 200)
                        return fail('Nie udało się zarchiwizować ankiety', response)
                      await refresh()
                    } catch {
                      reportFailure('Nie udało się zarchiwizować ankiety')
                    }
                  }}
                >
                  <Archive data-icon="inline-start" /> Archiwizuj
                </Button>
              )}
              {state === 'published' && (
                <>
                  <Field className="min-w-64">
                    <FieldLabel htmlFor="poll-cancel-reason">Powód anulowania</FieldLabel>
                    <Input
                      id="poll-cancel-reason"
                      value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)}
                    />
                  </Field>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={pending || !cancelReason.trim()}
                    onClick={async () => {
                      setActionError(undefined)
                      try {
                        const response = await cancelMutation.mutateAsync({
                          id: poll.id,
                          data: { reason: cancelReason.trim() },
                        })
                        if (response.status !== 200)
                          return fail('Nie udało się anulować ankiety', response)
                        await refresh()
                      } catch {
                        reportFailure('Nie udało się anulować ankiety')
                      }
                    }}
                  >
                    Anuluj ankietę
                  </Button>
                </>
              )}
            </FieldGroup>
          </CardContent>
        </Card>
        {state === 'draft' ? (
          <PollCapabilityGate capabilities={capabilities}>
            <PollForm
              capabilities={capabilities}
              initialValues={fromAdminPoll(poll)}
              submitLabel="Zapisz szkic"
              pending={updateMutation.isPending}
              onDirtyChange={setDraftDirty}
              onSubmit={async (payload) => {
                setActionError(undefined)
                try {
                  const response = await updateMutation.mutateAsync({
                    id: poll.id,
                    data: toAdminPollWriteRequest(payload),
                  })
                  if (response.status !== 200) {
                    fail('Nie udało się zapisać szkicu', response)
                    return false
                  }
                  await refresh()
                  return true
                } catch {
                  reportFailure('Nie udało się zapisać szkicu')
                  return false
                }
              }}
              onCancel={() => void navigate({ to: '/zadania/ankiety' })}
            />
          </PollCapabilityGate>
        ) : (
          <FrozenPollConfiguration poll={poll} />
        )}
        {results && (
          <PollResultsCard pollName={poll.name} questions={poll.questions} results={results} />
        )}
        <PollPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          poll={previewPayload}
          preview={preview}
          pending={publishMutation.isPending}
          error={actionError}
          onConfirm={async () => {
            if (draftDirty) return
            setActionError(undefined)
            try {
              const response = await publishMutation.mutateAsync({ id: poll.id })
              if (response.status !== 200)
                return fail('Nie udało się opublikować ankiety', response)
              setPreviewOpen(false)
              await refresh()
            } catch {
              reportFailure('Nie udało się opublikować ankiety')
            }
          }}
        />
      </div>
    </>
  )
}

type PollActionResponse =
  | V1PollsAdminPollsArchiveCreateMutationResult
  | V1PollsAdminPollsCancelCreateMutationResult
  | V1PollsAdminPollsCancelScheduleCreateMutationResult
  | V1PollsAdminPollsDestroyMutationResult
  | V1PollsAdminPollsPublishCreateMutationResult
  | V1PollsAdminPollsScheduleCreateMutationResult
  | V1PollsAdminPollsUpdateMutationResult

function FrozenPollConfiguration({ poll }: { poll: AdminPoll }) {
  const scope =
    poll.scope_kind === 'allClasses'
      ? 'Wszystkie aktywne klasy'
      : poll.scope_kind === 'selectedSchools'
        ? `Wybrane szkoły (${poll.school_ids.length})`
        : `Wybrane klasy (${poll.class_ids.length})`
  return (
    <Card>
      <CardHeader>
        <CardTitle>Zamrożona konfiguracja</CardTitle>
        <CardDescription>
          Ta konfiguracja jest tylko do odczytu po opuszczeniu szkicu.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 text-sm md:grid-cols-2">
        <div>
          <p className="text-muted-foreground">Zakres</p>
          <p>{scope}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Pytania</p>
          <p>{poll.questions.length}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Dostępna do</p>
          <p>{poll.available_until ?? 'Bez terminu'}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Przypomnienie</p>
          <p>{poll.reminder_at ?? 'Brak'}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Dystrybucja</p>
          <p>
            {poll.distribution.accepted_count}/{poll.distribution.attempted_count} zaakceptowanych
          </p>
          {failureBreakdownSummary(poll.distribution.failures) && (
            <p className="text-xs text-destructive">
              Przyczyny błędów: {failureBreakdownSummary(poll.distribution.failures)}
            </p>
          )}
        </div>
        <div>
          <p className="text-muted-foreground">Status przypomnienia</p>
          <p>{poll.reminder_status ?? 'Brak'}</p>
          {failureBreakdownSummary(poll.reminder_failures) && (
            <p className="text-xs text-destructive">
              Przyczyny błędów: {failureBreakdownSummary(poll.reminder_failures)}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
